import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useUiStore } from '@/store/ui';
import { chainGet, chainPost, fmtDpb, shortAddr } from '@/chainApi';
import { TX_LABEL } from '@shared/chain';
import {
  genWallet,
  openVault,
  saveVault,
  hasVault,
  signTx,
  importKeyFromSeed,
  rand,
  buf2hex,
  type ChainWallet,
} from '@/chainwallet/crypto';

interface AccountInfo {
  address: string;
  balance: number;
  dpx?: number;
  nonce?: number;
  txs?: unknown[];
}

const ADDR_RE = /^0x[0-9a-f]{40}$/;

/** 独立钱包：DP 链多钱包管理（口令加密密钥库 + 本地签名转账 + 稳定币）。 */
export function ChainWalletPage(): JSX.Element {
  const [unlocked, setUnlocked] = useState(false);
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [wallets, setWallets] = useState<ChainWallet[]>([]);
  const [activeAddr, setActiveAddr] = useState('');
  const [acc, setAcc] = useState<AccountInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [seedInput, setSeedInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  // 转账
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [token, setToken] = useState<'DPB' | 'DPX'>('DPB');
  // 稳定币
  const [mintDpx, setMintDpx] = useState('');
  const [redeemDpx, setRedeemDpx] = useState('');
  // 交易记录
  const [history, setHistory] = useState<unknown[]>([]);

  const persist = useCallback(
    async (list: ChainWallet[]): Promise<void> => {
      await saveVault(pwd, list);
      setWallets(list);
    },
    [pwd],
  );

  const refreshAccount = useCallback(
    async (addr: string): Promise<void> => {
      try {
        const raw = await chainGet<Record<string, unknown>>('api/account/' + addr);
        const info: AccountInfo = {
          address: String(raw.address ?? addr),
          balance: Number(raw.balance ?? 0),
          dpx: raw.dpx != null ? Number(raw.dpx) : undefined,
          nonce: raw.nonce != null ? Number(raw.nonce) : undefined,
          txs: Array.isArray(raw.txs) ? raw.txs : undefined,
        };
        setAcc(info);
        if (info.txs) setHistory(info.txs.slice(0, 10));
      } catch (err) {
        useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '未知错误');
      }
    },
    [],
  );

  useEffect(() => {
    if (unlocked && activeAddr) void refreshAccount(activeAddr);
  }, [unlocked, activeAddr, refreshAccount]);

  const unlock = async (): Promise<void> => {
    if (!pwd) return;
    setBusy(true);
    try {
      if (hasVault()) {
        const list = await openVault(pwd);
        setWallets(list);
        setActiveAddr(list[0]?.address ?? '');
        if (list.length > 0) await refreshAccount(list[0].address);
      } else {
        if (pwd !== pwd2) throw new Error('两次口令不一致');
        await saveVault(pwd, []);
        setWallets([]);
      }
      setUnlocked(true);
      useUiStore.getState().pushToast('success', '钱包已解锁');
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '口令错误或数据损坏');
    } finally {
      setBusy(false);
    }
  };

  const createWallet = async (): Promise<void> => {
    setBusy(true);
    try {
      const w = await genWallet(nameInput.trim() || '钱包 ' + (wallets.length + 1));
      const next = [...wallets, w];
      await persist(next);
      setActiveAddr(w.address);
      setNameInput('');
      await refreshAccount(w.address);
      useUiStore.getState().pushToast('success', `已创建 ${shortAddr(w.address)}（seed 见下方备份）`);
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '未知错误');
    } finally {
      setBusy(false);
    }
  };

  const importWallet = async (): Promise<void> => {
    const seed = seedInput.trim().toLowerCase();
    if (!/^[0-9a-f]{64}$/.test(seed)) {
      useUiStore.getState().pushToast('error', 'seed 格式：64 位十六进制');
      return;
    }
    setBusy(true);
    try {
      // 用用户提供的 seed 重建地址/pub（签名仍走 WebCrypto）
      const imported = await importKeyFromSeed(seed);
      const w2: ChainWallet = { name: nameInput.trim() || '导入钱包', seedHex: seed, pubHex: imported.pubHex, address: imported.address };
      if (wallets.some((x) => x.address === w2.address)) {
        useUiStore.getState().pushToast('error', '该钱包已存在');
        return;
      }
      const next = [...wallets, w2];
      await persist(next);
      setActiveAddr(w2.address);
      setSeedInput('');
      setNameInput('');
      await refreshAccount(w2.address);
      useUiStore.getState().pushToast('success', '已导入 ' + shortAddr(w2.address));
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '未知错误');
    } finally {
      setBusy(false);
    }
  };

  const removeWallet = async (addr: string): Promise<void> => {
    const next = wallets.filter((x) => x.address !== addr);
    await persist(next);
    if (activeAddr === addr) {
      setActiveAddr(next[0]?.address ?? '');
      if (next[0]) await refreshAccount(next[0].address);
    }
    useUiStore.getState().pushToast('success', '已删除钱包');
  };

  const submitTx = async (tx: Record<string, unknown>, okMsg: string): Promise<void> => {
    const w = wallets.find((x) => x.address === tx.from);
    if (!w) throw new Error('钱包不存在');
    tx.amount = Math.trunc(Number(tx.amount));
    tx.nonce = buf2hex(rand(16));
    const signed = await signTx(w, tx);
    const r = await chainPost<{ rejected?: { reason: string }[] }>('api/transactions', signed);
    if (r.rejected && r.rejected.length) throw new Error(r.rejected.map((x) => x.reason).join('；'));
    useUiStore.getState().pushToast('success', okMsg);
    await refreshAccount(w.address);
  };

  const doTransfer = async (): Promise<void> => {
    if (!activeAddr) return;
    const t = to.trim().toLowerCase();
    const amt = Math.trunc(Number(amount) * 1e6);
    if (!ADDR_RE.test(t)) {
      useUiStore.getState().pushToast('error', '收款地址格式错误');
      return;
    }
    if (!(amt > 0) || !Number.isFinite(amt)) {
      useUiStore.getState().pushToast('error', '金额必须为正数');
      return;
    }
    if (t === activeAddr.toLowerCase()) {
      useUiStore.getState().pushToast('error', '不能转给自己');
      return;
    }
    setBusy(true);
    try {
      await submitTx(
        token === 'DPX' ? { type: 'transfer', from: activeAddr, to: t, amount: amt, token: 'dpx' } : { type: 'transfer', from: activeAddr, to: t, amount: amt },
        `已提交转账 ${amount} ${token}，下个区块到账`,
      );
      setTo('');
      setAmount('');
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '未知错误');
    } finally {
      setBusy(false);
    }
  };

  const doMintDpx = async (): Promise<void> => {
    if (!activeAddr || !mintDpx) return;
    setBusy(true);
    try {
      const dpx = Number(mintDpx);
      if (!(dpx > 0) || !Number.isFinite(dpx)) throw new Error('铸造金额必须为正数');
      // 链端 canonical 对 dpx 做 ×1e6 微单位换算，这里传 DPB 数值
      await submitTx(
        { type: 'mint_dpx', from: activeAddr, dpx, price: 1, ratio: 1.5 },
        `已提交稳定币铸造 ${dpx} DPX（超额抵押 1.5×）`,
      );
      setMintDpx('');
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '未知错误');
    } finally {
      setBusy(false);
    }
  };

  const doRedeemDpx = async (): Promise<void> => {
    if (!activeAddr || !redeemDpx) return;
    setBusy(true);
    try {
      const burn = Number(redeemDpx);
      if (!(burn > 0) || !Number.isFinite(burn)) throw new Error('赎回金额必须为正数');
      // 链端 canonical 对 burn 做 ×1e6 微单位换算，这里传 DPB 数值
      await submitTx(
        { type: 'redeem_dpx', from: activeAddr, burn, price: 1 },
        `已提交赎回 ${burn} DPX → DPB`,
      );
      setRedeemDpx('');
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '未知错误');
    } finally {
      setBusy(false);
    }
  };

  const doFaucet = async (): Promise<void> => {
    if (!activeAddr) return;
    setBusy(true);
    try {
      await submitTx({ type: 'mint', from: activeAddr, to: activeAddr, amount: 100 }, '已领取新手 DPB，等待出块确认');
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '未知错误');
    } finally {
      setBusy(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="card mx-auto max-w-md p-6">
        <h1 className="text-base font-medium text-text">独立钱包 · DP Chain</h1>
        <p className="mt-1 text-xs text-dim">多钱包密钥库（口令 PBKDF2+AES-GCM 加密，私钥永不上传；与 MeshLink 身份钱包相互独立）</p>
        <div className="mt-4 space-y-3">
          <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="设置 / 输入口令" />
          {!hasVault() ? (
            <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} placeholder="再次输入口令（创建密钥库）" />
          ) : null}
          <Button className="w-full" loading={busy} onClick={() => void unlock()}>
            {hasVault() ? '解锁钱包' : '创建密钥库并进入'}
          </Button>
        </div>
      </div>
    );
  }

  const active = wallets.find((x) => x.address === activeAddr);

  return (
    <div className="grid gap-4">
      <div className="card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-base font-medium text-text">独立钱包 · DP Chain</h1>
          <Badge tone="ok">密钥库已解锁</Badge>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {wallets.map((w) => (
            <button
              key={w.address}
              type="button"
              onClick={() => {
                setActiveAddr(w.address);
                void refreshAccount(w.address);
              }}
              className={`rounded-btn px-3 py-1.5 text-xs ${
                w.address === activeAddr ? 'bg-accent text-[#04141c]' : 'bg-panel-2 text-dim hover:bg-line'
              }`}
            >
              {w.name} · {shortAddr(w.address)}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Input value={nameInput} onChange={(e) => setNameInput(e.target.value)} placeholder="钱包名（可选）" className="w-36" />
          <Button size="sm" loading={busy} onClick={() => void createWallet()}>
            + 创建钱包
          </Button>
          <Input
            value={seedInput}
            onChange={(e) => setSeedInput(e.target.value)}
            placeholder="64 位 seedHex 导入"
            className="w-56 font-mono"
          />
          <Button size="sm" variant="ghost" loading={busy} onClick={() => void importWallet()}>
            导入
          </Button>
        </div>
      </div>

      {active ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="card p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-text">余额 · {active.name}</h2>
              <button type="button" className="text-xs text-err hover:underline" onClick={() => void removeWallet(active.address)}>
                删除该钱包
              </button>
            </div>
            <p className="mt-1 break-all font-mono text-[11px] text-dim">{active.address}</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-btn bg-panel-2 p-3">
                <p className="text-[11px] text-dim">DPB 余额</p>
                <p className="mt-1 text-lg font-medium text-ok">{fmtDpb(acc?.balance ?? 0)}</p>
              </div>
              <div className="rounded-btn bg-panel-2 p-3">
                <p className="text-[11px] text-dim">DPX 稳定币</p>
                <p className="mt-1 text-lg font-medium text-ok">{fmtDpb(acc?.dpx ?? 0)}</p>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-dim">
              备份 seed（请抄录保存）：
              <span className="break-all font-mono text-text">{active.seedHex}</span>
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="ghost" loading={busy} onClick={() => void doFaucet()}>
                水龙头 100 DPB
              </Button>
              <Button size="sm" variant="ghost" onClick={() => void refreshAccount(active.address)}>
                刷新
              </Button>
            </div>
          </div>

          <div className="card p-5">
            <h2 className="mb-3 text-sm font-medium text-text">转账（本地签名后提交）</h2>
            <div className="space-y-2">
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="0x… 收款地址" className="font-mono" />
              <div className="flex gap-2">
                <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="金额（DPB/DPX）" />
                <select
                  value={token}
                  onChange={(e) => setToken(e.target.value as 'DPB' | 'DPX')}
                  className="rounded-btn border border-line bg-panel-2 px-2 text-xs text-text"
                >
                  <option value="DPB">DPB</option>
                  <option value="DPX">DPX</option>
                </select>
              </div>
              <Button className="w-full" loading={busy} onClick={() => void doTransfer()}>
                签名并提交转账
              </Button>
            </div>

            <h2 className="mb-2 mt-5 text-sm font-medium text-text">稳定币 DPX</h2>
            <div className="grid grid-cols-2 gap-2">
              <Input value={mintDpx} onChange={(e) => setMintDpx(e.target.value)} placeholder="DPB 抵押额 → 铸造 DPX" />
              <Button size="sm" loading={busy} onClick={() => void doMintDpx()}>
                铸造 DPX
              </Button>
              <Input value={redeemDpx} onChange={(e) => setRedeemDpx(e.target.value)} placeholder="DPX 赎回额 → DPB" />
              <Button size="sm" variant="ghost" loading={busy} onClick={() => void doRedeemDpx()}>
                赎回 DPX
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="card p-5">
          <p className="py-2 text-xs text-dim">先创建或导入钱包</p>
        </div>
      )}

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">最近交易</h2>
        {history.length === 0 ? (
          <p className="py-2 text-xs text-dim">暂无交易记录</p>
        ) : (
          <div className="max-h-[280px] space-y-1 overflow-y-auto">
            {history.map((t, i) => {
              const rec = t as Record<string, unknown>;
              const type = String(rec.type ?? '');
              const from = String(rec.from ?? '').toLowerCase();
              const to = String(rec.to ?? '').toLowerCase();
              const self = activeAddr.toLowerCase();
              let dir: 'in' | 'out' | 'na' = 'in';
              let sign = '+';
              if (type === 'transfer') {
                if (from === self && to !== self) {
                  dir = 'out';
                  sign = '−';
                } else if (to === self && from !== self) {
                  dir = 'in';
                  sign = '+';
                } else {
                  dir = 'na';
                  sign = '';
                }
              }
              const amount = rec.amountMicro != null ? fmtDpb(Number(rec.amountMicro)) : '—';
              const tone = dir === 'out' ? 'text-err' : dir === 'in' ? 'text-ok' : 'text-dim';
              return (
                <div
                  key={i}
                  className="flex items-center justify-between rounded-btn bg-panel-2 px-2 py-1.5 text-[11px]"
                >
                  <span className="text-text">{TX_LABEL[type] ?? type}</span>
                  <span className={`mono ${tone}`}>
                    {rec.amountMicro != null ? `${sign}${amount} DPB` : '—'}
                  </span>
                  <span className="text-dim">{to ? `${to.slice(0, 10)}…` : '—'}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChainWalletPage;
